import { useRef, useEffect } from 'react';

import Typography from 'components/Typography';
import { IconClose } from 'components/Icons';

import { htmlId } from 'utils/html';
import { ANALYTICS } from 'analytics/constants';
import { useAnalytics } from 'hooks/useAnalytics';
import { useVendorCommand } from 'hooks/useVendorCommand';

import { config } from './content';
import {
  StyledButtons,
  StyledButton,
  StyledCancelButton,
  StyledConfirmationContainer,
  StyledDialog,
  StyledIconButton,
  StyledTextContainer,
  ModalHeader,
} from './EndAdvocateChatModal.styles';
import { EndAdvocateChatModalProps } from './EndAdvocateChatModal.types';

const EndAdvocateChatModal = ({
  handleHideEndChat,
  toggleEndChatModal,
  modalType,
}: EndAdvocateChatModalProps) => {
  const { trackEvent } = useAnalytics();
  const { runVendorCommandAsync } = useVendorCommand();

  const overlayRef = useRef<HTMLDivElement>(null);

  const { title, body, cancel, submitButton, vendorCommand, containerId, analtyics } =
    config[modalType];

  useEffect(() => {
    const handleClickOverlay = (event: MouseEvent) => {
      if (overlayRef?.current?.id === (event.target as Element).id) {
        trackEvent({
          analyticEvent: ANALYTICS.EVENT.CLICKED,
          details: {
            analytic_click_id: analtyics.overlayClickId,
          },
        });
        toggleEndChatModal();
      }
    };
    document.addEventListener('mousedown', handleClickOverlay);
    return () => {
      document.removeEventListener('mousedown', handleClickOverlay);
    };
  }, [overlayRef]);

  const handleSubmit = async () => {
    await runVendorCommandAsync(vendorCommand);
  };

  const handleOnClickSubmitButton = () => {
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: analtyics.submitClickId,
        content: submitButton,
      },
    });
    handleSubmit();
    toggleEndChatModal();
    handleHideEndChat(true);
  };

  const handleOnClickCancel = () => {
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: analtyics.cancelClickId,
        content: cancel,
      },
    });
    toggleEndChatModal();
  };

  const handleOnClickX = () => {
    trackEvent({
      analyticEvent: ANALYTICS.EVENT.CLICKED,
      details: {
        analytic_click_id: analtyics.closeClickId,
        content: 'X',
      },
    });
    toggleEndChatModal();
  };

  return (
    <StyledConfirmationContainer id={htmlId(containerId)} ref={overlayRef}>
      <StyledDialog>
        <ModalHeader>
          <StyledIconButton>
            <IconClose onClick={handleOnClickX} />
          </StyledIconButton>
        </ModalHeader>
        <StyledTextContainer>
          <Typography as='h2' variant='heading' size='md' color='strong'>
            {title}
          </Typography>
          <Typography as='p' size='md' variant='body'>
            {body}
          </Typography>
        </StyledTextContainer>
        <StyledButtons>
          <StyledCancelButton priority='primary' variant='inverse' onClick={handleOnClickCancel}>
            <Typography as='span' size='md' variant='label' color='primary'>
              {cancel}
            </Typography>
          </StyledCancelButton>
          <StyledButton
            priority='primary'
            variant='destructive'
            onClick={handleOnClickSubmitButton}
            style={{ minWidth: 'fit-content' }}
          >
            <Typography as='span' size='md' variant='label' color='inverse'>
              {submitButton}
            </Typography>
          </StyledButton>
        </StyledButtons>
      </StyledDialog>
    </StyledConfirmationContainer>
  );
};

export default EndAdvocateChatModal;
